import { ComponentType } from '@grafikr/components';
import { debounceTime, fromEvent, throttleTime } from 'rxjs';
import { first } from 'rxjs/operators';
import mobileCheck from 'utils/mobile-check';
import getIntersectionPercentage from 'utils/get-intersection-percentage';
import Drawer from '../utils/components/drawer';

const component: ComponentType = (node) => {
  const scrollContainer = node.querySelector('main');
  const sections = Array.from(scrollContainer.querySelectorAll<HTMLElement>('.horizontal-section'));

  const chapterToggle = document.querySelector('[data-chapter-toggle]');
  const chapterText = chapterToggle.querySelector('[data-text]');
  const chapter = chapterToggle.querySelector<HTMLElement>('[data-chapter]');
  const chapterTitle = chapterToggle.querySelector('[data-title]');
  const chapterBubble = chapterToggle.querySelector('[data-bubble]');

  let previousSection: HTMLElement;
  let deltaY = 0;

  const update = (direction: 1 | -1, targetTranslate?: number) => {
    scrollContainer.classList.remove(direction === 1 ? 'backwards' : 'forwards');
    scrollContainer.classList.add(direction === 1 ? 'forwards' : 'backwards');

    const currentTranslate = scrollContainer.style.transform.length
      ? parseInt(scrollContainer.style.transform.split('(')[1].split(',')[0], 10)
      : 0;

    const difference = currentTranslate - (targetTranslate || 0);

    const offset = {
      x: difference,
      y: 0,
    };

    const activeSection = sections.reduce(
      (result, current) =>
        getIntersectionPercentage(current, offset) > getIntersectionPercentage(result, offset)
          ? current
          : result,
      sections[0],
    );

    document.body.style.backgroundColor = activeSection?.dataset.color;
    document.body.classList.remove(...theme.classes.body.floatingHeaderLight.split(' '));
    document.body.classList.remove(...theme.classes.body.floatingHeaderDark.split(' '));
    document.body.classList.add(...activeSection?.dataset.headerClass.split(' ') || '');

    const isLight = activeSection?.dataset.headerClass.includes('light');

    chapterBubble.classList.remove(
      ...(isLight ? ['bg-black', 'text-white'] : ['bg-brand-sand', 'text-black']),
    );
    chapterBubble.classList.add(
      ...(isLight ? ['bg-brand-sand', 'text-black'] : ['bg-black', 'text-white']),
    );

    chapterText.classList.remove(...(isLight ? ['text-black'] : ['text-white']));
    chapterText.classList.add(...(isLight ? ['text-white'] : ['text-black']));

    chapter.textContent = parseInt(activeSection.dataset.chapter, 10) !== 0
      ? chapter.dataset.chapter.replace('0', activeSection.dataset.chapter)
      : '';

    chapterTitle.textContent = activeSection?.dataset.chapterTitle || chapterTitle.textContent;

    const previousSectionProspect = sections.find((section) =>
      section.classList.contains('active'),
    );

    previousSection =
      previousSectionProspect !== activeSection ? previousSectionProspect : previousSection;

    sections.forEach((section) => {
      section.classList[section === previousSection ? 'add' : 'remove']('previous');
      section.classList[section === activeSection ? 'add' : 'remove']('active');
    });

    if (typeof targetTranslate === 'number') {
      scrollContainer.style.transform = `translate3d(${targetTranslate}px, 0, 0)`;
    }
  };

  const onScroll = (direction: 1 | -1) => {
    if (mobileCheck()) {
      update(direction);
    } else {
      const limit = (scrollContainer.scrollWidth - window.innerWidth) * -1;

      const currentTranslate = scrollContainer.style.transform.length
        ? parseInt(scrollContainer.style.transform.split('(')[1].split(',')[0], 10)
        : 0;

      // * -1 because we always want negative translate to simulate rightwards scrolling
      let targetTranslate = currentTranslate + window.innerWidth * direction * -1;

      if (targetTranslate < limit) {
        targetTranslate = limit;
      }

      if (targetTranslate > 0) {
        targetTranslate = 0;
      }

      update(direction, targetTranslate);
    }
  };

  const reset = () => {
    let previousY = window.scrollY;

    if (mobileCheck()) {
      scrollContainer.style.transform = null;

      // @ts-ignore
      const subscription = fromEvent(window, 'scroll')
        .pipe(throttleTime(80))
        .subscribe(() => {
          deltaY = window.scrollY - previousY;
          const direction = deltaY >= 0 ? 1 : -1;

          onScroll(direction);

          previousY = window.scrollY;
        });

      // @ts-ignore
      fromEvent(window, 'resize')
        .pipe(debounceTime(200))
        .pipe(first())
        .subscribe(() => {
          subscription.unsubscribe();
          reset();
        });
    } else {
      window.scrollTo(0, 0);
      window.dispatchEvent(new Event('scroll'));

      // @ts-ignore
      const createSectionScroller = () =>
        fromEvent(scrollContainer, 'wheel')
          .pipe(first())
          .subscribe((event: WheelEvent) => {
            const direction = event.deltaY >= 0 ? 1 : -1;
            onScroll(direction);
          });

      let sectionScroller = createSectionScroller();

      // @ts-ignore
      const wheelListener = fromEvent(scrollContainer, 'wheel')
        .pipe(debounceTime(200))
        .subscribe(() => {
          sectionScroller.unsubscribe();
          sectionScroller = createSectionScroller();
        });

      const onKeyUp = (event: KeyboardEvent) => {
        if (['ArrowRight', 'ArrowLeft'].includes(event.key)) {
          const direction = event.key === 'ArrowRight' ? 1 : -1;
          onScroll(direction);
        }
      };

      document.addEventListener('keyup', onKeyUp);

      // @ts-ignore
      fromEvent(window, 'resize')
        .pipe(debounceTime(200))
        .pipe(first())
        .subscribe(() => {
          sectionScroller.unsubscribe();
          wheelListener.unsubscribe();
          document.removeEventListener('keyup', onKeyUp);

          reset();
        });
    }
  };

  const chapterSectionElms: NodeListOf<HTMLElement> = scrollContainer.querySelectorAll(
    '.shopify-section.section > * > :first-child',
  );
  const chapterSections = [];

  chapterSectionElms.forEach((section) => {
    chapterSections.push({
      element: section,
      x: section.getBoundingClientRect().x,
    });
  });

  function renderNavigation() {
    const navigationContainer: HTMLElement = document.querySelector('[data-chapter-menu]');
    const navigation = Drawer(navigationContainer);
    const toggles = document.querySelectorAll('[data-chapter-toggle]');

    toggles.forEach((toggle) => {
      toggle.addEventListener('click', () => {
        navigation.toggle();
      });
    });

    chapterSections.forEach((section) => {
      const { element } = section;
      const button = document.createElement('button');

      button.type = 'button';
      button.setAttribute(
        'class',
        'px-2 py-4 lg:px-12 lg:py-8 grid grid-cols-2 gap-6 hover-supported:hover:bg-brand-sand hover-supported:hover:text-black transition-colors',
      );
      button.setAttribute('data-to', element.dataset.chapter);

      button.innerHTML = `<span class="text-left lg:text-2xl">${element.dataset.chapterTitle}</span>
        <span class="text-sm lg:text-xl whitespace-nowrap grid grid-cols-2 gap-6">
          ${
            parseInt(element.dataset.chapter, 10) !== 0
              ? `<span class="text-left">${chapter.dataset.chapter.replace('0', element.dataset.chapter)}</span>`
              : ''
          }

          ${
            element.dataset.chapterMenuDescription
              ? `<span class="text-right">${element.dataset.chapterMenuDescription}</span>`
              : ''
          }
        </span>`;

      button.addEventListener('click', () => {
        const elm = chapterSections.find(
          (cur) => cur.element.dataset.chapter === button.dataset.to,
        );
        if (elm) {
          sections.forEach((i) => i.classList.remove('active', 'previous'));
          navigation.close();
          elm.element.classList.add('active');
          deltaY = elm.x;
          if (mobileCheck()) {
            elm.element.scrollIntoView();
          } else {
            scrollContainer.style.transitionProperty = 'none';
            scrollContainer.style.transform = `translate3d(-${elm.x}px, 0, 0)`;
            setTimeout(() => {
              scrollContainer.style.transitionProperty = null;
            }, 0);
          }
        }

        chapter.textContent = parseInt(element.dataset.chapter, 10) !== 0
          ? chapter.dataset.chapter.replace('0', element.dataset.chapter)
          : '';

        chapterTitle.textContent = element?.dataset.chapterTitle || chapterTitle.textContent;
      });

      navigationContainer.insertAdjacentElement('beforeend', button);
    });
  }

  const init = () => {
    reset();
    update(1);
    renderNavigation();
  };

  init();
};

export default component;
